import React, { useState, useEffect } from 'react';
import queryString from 'query-string';
import Cookies from 'js-cookie';
import { SupportedURLParams } from 'types';
import { supportedUTMParams } from '@landr/core';
import { UTMCookieSettings } from '../../../constants';
import { getRegistrationFormUrl } from '../../../helpers';
import { SignupProps } from '.';

export enum RegistrationFormEventsEnum {
    Login = 'LoginSuccessful',
    Registration = 'RegistrationSuccessful',
}

const makeCollaborationReturnPath = (id: string, type: string, code: string): string => {
    if (type === 'Audio') {
        return `/library/tracks/${id}?code=${code}&referrer=authsite`;
    } else if (type === 'Project') {
        return `/library/projects/${id}?code=${code}&referrer=authsite`;
    } else {
        return '';
    }
};

const fetchCollaborationReturnPath = (code: string): Promise<string> => {
    return fetch(`${process.env.GATSBY_API_ENDPOINT}/library/api/v1/collaborators/link/${code}`)
        .then(response => response.json())
        .then(item => (item ? makeCollaborationReturnPath(item.itemId, item.itemType, code) : ''))
        .catch(() => {
            return '';
        });
};

const makeRedirectUrl = async (params: SupportedURLParams): Promise<string> => {
    const appEndpoint = process.env.GATSBY_APP_ENDPOINT as string;

    // Determine where to redirect the user after registering & logging in
    if (params && params.plan) {
        // User has choosen a billing plan
        return `${appEndpoint}/purchase/cart/credit/${params.plan}/${params.planFrequency ||
            'yearly'}`;
    } else if (params && params.code) {
        // Invitation to collaborate on an Asset/Project
        return appEndpoint + (await fetchCollaborationReturnPath(params.code));
    } else if (params && params.returnUrl) {
        // User was going somewhere on the App while logged out.
        return params.returnUrl;
    } else {
        // Send the user to the home page of the app
        return appEndpoint;
    }
};

export const withRegistrationForm = (WrappedComponent: React.ComponentType<SignupProps>) => {
    return (props: SignupProps) => {
        const params = queryString.parse(props.location.search);
        const [iframeURL, setIframeURL] = useState('');

        const redirectToApp = (e: MessageEvent): void => {
            const event = e.data;
            if (
                event.type === RegistrationFormEventsEnum.Registration ||
                event.type === RegistrationFormEventsEnum.Login
            ) {
                // Delete the UTMs stored in the cookies if a user has registered or logged in
                // So we don't make duplicate campaign convertions
                supportedUTMParams.forEach((param: string) => {
                    Cookies.remove(param, UTMCookieSettings);
                });
                setTimeout(async () => {
                    const redirectUrl = await makeRedirectUrl(params);
                    window.top.location.assign(redirectUrl);
                }, 1500);
            }
        };

        useEffect(() => {
            if (props.context.isLoggedIn) {
                makeRedirectUrl(params).then(url => window.top.location.assign(url));
            }
        }, [props.context.isLoggedIn]);

        useEffect(() => {
            window.addEventListener('message', redirectToApp);

            return () => window.removeEventListener('message', redirectToApp);
        }, []);

        const makeRegistrationFormUrl = () => {
            const { lang } = props.context.pageContext;
            const route = props.slice.primary.show_login_first === 'Yes' ? '/login' : '';
            let UTMs: { [key: string]: string } = {};
            supportedUTMParams.forEach((param: string) => {
                if (Cookies.get(param)) {
                    UTMs[param] = Cookies.get(param) as string;
                }
            });
            return getRegistrationFormUrl(lang, { ...UTMs, ...params }, route);
        };

        useEffect(() => {
            /**
             * Note that we are not setting the iframe URL for the initial render of the component since we need the generated HTML file to not contain an iframe URL. This is because we need to be able to generate an iframe URL based on the browser's query params (e.g. UTMs) and then render the iframe with the correct URL. If the iframe is created with a URL that doesn't contain the correct parameters, it's impossible to change the contents of the iframe by passing a new iframe URL.
             *
             * So when we build the app, there will not be an iframe generated, but when we mount the component, React will compute an iframeURL based on the current URL parameters of the user and then render the correct iframe.
             *
             * If you decide to change this part of the code, it will be important to test that the UTMs are still being passed to the registration form in a production environment. Here are the steps to test that:
             * - Create a production build using `yarn build`
             * - Serve the production build locally using `yarn serve`
             * - Go to the signup page and pass a query parameter of `partner` with a value of `true` (i.e. http://local.landr.com:9000/en/signup/?partner=true)
             * - Inspect the iframe src attribute and make sure that the value of the attribute includes the same partner=true parameter. Also the iframe should render the registration form in an "email signup only" mode, meaning that you can only signup with an email when you pass a `partner` query parameter to the iframe
             */
            setIframeURL(makeRegistrationFormUrl() || '');
        }, [props.location.search]);

        return <WrappedComponent params={params} iframeURL={iframeURL} {...props} />;
    };
};
